import {
  Component,
  Input,
  type OnInit,
  signal,
  type WritableSignal,
  inject,
  Output,
  EventEmitter
} from '@angular/core'
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog'
import { TriggerApiCallsService } from '../../core/services/cancel-api-call'
import { SizesService } from '../../core/services/sizes.service'
import { ToastAlertComponent } from '../../03_shared-components/01_alerts/toast-alert/toast-alert.component'
import {
  type AbstractControl,
  type FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  type ValidationErrors,
  type ValidatorFn,
  Validators
} from '@angular/forms'
import { type MatAutocompleteSelectedEvent } from '@angular/material/autocomplete'
import {
  CheckIsFloat,
  // CheckIsFloat,
  CheckIsInteger
} from 'src/app/03_shared-components/forms/form-error-handling/form-validators'
import { type MatChipInputEvent } from '@angular/material/chips'
import { LiveAnnouncer } from '@angular/cdk/a11y'

interface Parent {
  formGroup: FormGroup
  parent_id: number
  parent_title: string
  inputs: ParentInput[]
  allowedActions?: number[]
}

interface ParentInput {
  input_name: string
  input_type: string
  input_value?: any
  options: any[] | any // could be an object that maps first input to lists of options for second
  data_query: string
  multiple?: boolean
  chips?: WritableSignal<string[]>
}

interface ParentChoice {
  parent_id: number
  parent_category: string
  parent_title: string
  parent: string
  inputs: ParentInput[]
  options: any[] | any
  allowed_actions?: number[]
}

@Component({
  standalone: false,
  selector: 'app-action-condition-form',
  templateUrl: './action-condition-form.component.html',
  styleUrl: './action-condition-form.component.scss'
})
export class ActionConditionFormComponent implements OnInit {
  loading: boolean = true
  loadingMessage: string = 'loading'
  parentFormArray: FormArray
  announcer = inject(LiveAnnouncer)

  parents: Parent[] = []
  @Input() parentChoices: ParentChoice[]
  filteredParentChoices: any[] = []

  allowedActions: number[]
  @Output() updateAllowedActions: EventEmitter<number[]> = new EventEmitter<number[]>()

  @Input('parentChoices')
  set _parentChoices (data: ParentChoice[]) {
    this.parentChoices = data
    this.filterParentOptions(this.parentOptionsSearchTerm)
    const parentIds = data.map(parent => parent.parent_id)
    for (let i = 0; i < this.parents.length; i++) {
      if (this.parents[i].parent_id && !parentIds.includes(this.parents[i].parent_id)) {
        this.removeParent(i)
        i-- // splicing will leave a new element to check at this index, so we counteract the loop increment
      }
    }
  }

  parentOptionsSearchTerm: string

  filteredParentInputOptions: Record<string, any[]> = {}
  activeQueries: Record<string, string> = {}
  @Input() sizes: string[]

  @Input() parentTypeName: string
  @Input() tooltipText: string
  @Input() typePrefix: string = 'parent'

  ruleForm: FormGroup

  get title (): string {
    if (this.config?.data?.mode === 'copy') {
      return 'Copy Rule'
    } else if (this.config?.data?.mode === 'edit') {
      return 'Edit Rule'
    } else {
      return 'Add Rule'
    }
  }

  badProducts = {}

  constructor (
    public config: DynamicDialogConfig,
    public dialogService: DialogService,
    public triggerApiCallsService: TriggerApiCallsService,
    public toastAlert: ToastAlertComponent,
    private readonly formBuilder: FormBuilder,
    private readonly sizesService: SizesService
  ) {
    this.parentFormArray = this.formBuilder.array([], this.checkChildErrors())
    // this.ruleForm.addValidators(() => {
    // })
  }

  @Input('existingRuleArray')
  set _existingRuleArray (existingRuleArray: ParentChoice[]) {
    console.log('editing rule', existingRuleArray)
    if (!existingRuleArray) return
    for (let i = 0; i < existingRuleArray.length; i++) {
      const parent = this.convertExistingObject(
        JSON.parse(JSON.stringify(existingRuleArray[i])),
        this.parentTypeName.toLowerCase()
      )
      // const parentChoice = this.parentChoices.find(
      //   parentChoice => parentChoice.parent_id == condition.parent_id
      // )
      // this.addCondition(parentChoice, condition)

      console.log('for editing rule, adding parent', parent)
      parent.inputs = parent.action_inputs ? parent.action_inputs : parent.condition_inputs
      const parentChoice = this.parentChoices.find(parentChoice => parentChoice.parent_id == parent.id)
      console.log('found parentChoice', parentChoice)
      console.log('from parentChoices', this.parentChoices)
      parent.inputs.forEach((input, index) => {
        input['input_type'] = parentChoice.inputs[index].input_type
        console.log('input.input_value', input.input_value)
        if (this.parentTypeName === 'Condition') {
          if (input.input_type === 'boolean' && typeof input.input_value[0] !== 'boolean') {
            input.input_value = input.input_value.map(val => val.toLowerCase() === 'true')
          }
          if (input.input_type === 'pct') {
            input.input_value = input.input_value.map((val: string) => Number(val))
          }
          if (parentChoice.inputs[index].options) {
            const opts = parentChoice.inputs[index].options
            input.options = typeof opts === 'string' ? opts : [...opts]
          }
        } else {
          if (input.input_type === 'boolean') {
            input.input_value = input.input_value.toLowerCase() === 'true'
          }
          if (input.input_type === 'pct') {
            input.input_value = Number(input.input_value)
          }
          if (input.multiple) {
            input.input_value = JSON.parse(input.input_value)
          }
          if (parentChoice.inputs[index].options) {
            const opts = parentChoice.inputs[index].options
            input.options = typeof opts === 'string' ? opts : [...opts]
          }
        }
      })

      this.addParent(parentChoice, parent)
    }

    this.parents.forEach((parent: any, parentIndex: number) => {
      if (parent.inputs) {
        parent.inputs.forEach((input: any) => {
          if (input.options) {
            const key = `${parentIndex}-${input.input_name}`
            this.filteredParentInputOptions[key] = this.getConditionalInputOptions(input, parent, false)
          }
        })
      }
    })

    // pre-populate the form with the data from the given rule
    this.filteredParentChoices = this.parentChoices
  }

  ngOnInit (): void {
    console.log('this.parentChoices', this.parentChoices)
    this.filteredParentChoices = this.parentChoices

    this.loading = false
  }

  convertExistingObject (obj, prefix) {
    if (prefix === 'action') {
      obj.id = obj.action_id
      obj.inputs = obj.action_inputs
    } else if (prefix === 'condition') {
      obj.id = obj.condition_id
      obj.inputs = obj.action_inputs
    }
    return obj
  }

  // consoleprint (data) {
  //   console.log('consoleprint', data)
  // }

  stringify (data) {
    return JSON.stringify(data)
  }

  // get conditions (): FormArray {
  //   return this.ruleForm.get('conditions') as FormArray
  // }

  get name () {
    return this.ruleForm.get('name') as FormControl
  }

  get description () {
    return this.ruleForm.get('description') as FormControl
  }

  addParent (parentChoice = {}, parent: Parent = undefined) {
    console.log('adding parentChoice', parentChoice)
    // const buildingParent = {
    //   parent_id: condition?.parent_id,
    //   parent_title: condition?.parent_title,
    //   formGroup: this.formBuilder.group(
    //     {},
    //     {
    //       validators: this.checkChildErrors()
    //     }
    //   ),
    //   input_name: condition?.input_name,
    //   options: parentChoice['options'], // these are the input options
    //   datatype: condition?.datatype
    // }

    // buildingParent.formGroup.addControl('parent_title', new FormControl(parentChoice))

    // this.conditionFormArray.push(buildingParent['formGroup'])
    // this.conditions.push(buildingParent)

    // if (condition !== undefined) {
    //   console.log('adding input from edit')
    //   this.addConditionInput(buildingParent, parentChoice as ParentChoice, condition.input_value)
    // }
    const buildingParent = {
      formGroup: this.formBuilder.group(
        {
          parent_title: [parentChoice, [Validators.required]],
          inputs: new FormGroup({}, this.checkChildErrors())
        },
        {
          validators: this.checkChildErrors()
        }
      ),
      parent_id: parentChoice['parent_id'],
      parent_title: parentChoice['parent_title'],
      inputs: parent ? parent['inputs'] : [],
      allowedActions: parent ? parent['allowedActions'] : null
    }

    this.parentFormArray.push(buildingParent['formGroup'])
    this.parents.push(buildingParent)

    if (parent) {
      this.addParentInputs(parent['inputs'], buildingParent, parentChoice as ParentChoice)
    }
  }

  removeParent (i: number) {
    this.parentFormArray.removeAt(i)
    this.parents.splice(i, 1)

    // update allowed actions if they exist
    this.allowedActions = null
    this.parents.forEach(parent => {
      console.log('checking parent for allowed actions', parent)
      if (parent.allowedActions) {
        console.log('found allowed actions', parent.allowedActions)
        if (this.allowedActions?.length) {
          this.allowedActions = this.allowedActions.filter(action => parent.allowedActions.includes(action))
        } else {
          this.allowedActions = parent.allowedActions
        }
      }
    })
    this.updateAllowedActions.emit(this.allowedActions)
  }

  addParentInputs (inputs: ParentInput[], parent: Parent, parentChoice: ParentChoice = undefined) {
    const inputFormGroup = parent.formGroup.get('inputs') as FormGroup
    parent.inputs.forEach(input => {
      inputFormGroup.removeControl(input.input_name)
    })
    parent.formGroup.addControl('inputs', inputFormGroup)
    parent.inputs = []
    console.log('adding all parent inputs', inputs)
    for (let i = 0; i < inputs.length; i++) {
      const input = inputs[i]
      const validators = this.getValidators(input.input_type)

      if (input.data_query === 'products') {
        validators.push((control: AbstractControl) => {
          console.log('checking bad products', input)
          const key = parent.parent_id + '|' + input.input_name
          console.log('with key', key)
          console.log('and object', this.badProducts)
          return this.badProducts[key] && this.badProducts[key].length ? { badProducts: true } : null
        })
      }

      console.log('input.options', input.options)

      console.log('adding control of name', input.input_name)

      console.log('parentChoice when adding input', parentChoice)
      inputFormGroup.addControl(
        input.input_name,
        new FormControl(input.input_type === 'boolean' && input.input_name === '' ? true : input.input_value, {
          validators
        })
      )

      console.log('added form control', inputFormGroup.get(input.input_name))

      if (input.data_query === 'products') {
        (inputFormGroup.get(input.input_name) as FormControl).valueChanges.subscribe(val => {
          delete this.badProducts[parent.parent_id + '|' + input.input_name]
        })

        // .registerOnChange(() => {
        //   console.log('register change function control')
        //   delete this.badProducts[parent.parent_id + '|' + input.input_name]
        // })
      }

      parent.inputs.push({
        input_name: input.input_name,
        input_type: input.input_type,
        options: inputs[i].options,
        multiple: inputs[i].multiple,
        data_query: inputs[i].data_query,
        chips: signal(inputs[i].input_value)
      })

      console.log('pushed parent input', parent.inputs[parent.inputs.length - 1])
    }
  }

  // addConditionInput (condition: Condition, selectedOption: ParentChoice, value = undefined) {
  //   console.log('addconditioninput')
  //   console.log('condition', condition)
  //   console.log('selectedOption', selectedOption)

  //   const validators = this.getValidators(condition.datatype)
  //   console.log('validators', validators)
  //   condition.formGroup.addControl(
  //     condition.input_name,
  //     new FormControl(value !== undefined ? value : '', validators)
  //   )

  //   // inputs.push(this.formBuilder.control('', validators))
  //   // console.log('inputs', inputs)
  //   // console.log('validators', validators)
  // }

  //   async onSubmit () {
  //     this.loading = true
  //     console.log('this.name', this.name)
  //     console.log('this.ruleForm', this.ruleForm)
  //     console.log('this.description', this.description)

  //     const rule = {
  //       rule_name: this.name.value.trim(),
  //       rule_description: this.description.value,
  //       priority_order: this.config.data.existingRules.length,
  //       conditions: this.conditions.map((condition, index) => {
  //         // return {
  //         //   parent_id: condition.parent_id,
  //         //   input_name: condition.input_name,
  //         //   input_value: condition.formGroup.get(condition.input_name).value
  //         // }
  //         return {
  //           parent_id: condition.parent_id,
  //           condition_inputs: condition.inputs.map(input => {
  //             let value = condition.formGroup.get('inputs').get(input.input_name).value
  //             if (input.input_type === 'pct' && value) {
  //               value = value / 100
  //             }
  //             return {
  //               input_name: input.input_name,
  //               input_value: value
  //             }
  //           })
  //         }
  //       }),
  //       actions: this.actions.map((action, index) => {
  //         return {
  //           action_id: action.action_id,
  //           action_execution_order: index,
  //           action_inputs: action.inputs.map(input => {
  //             let value = action.formGroup.get('inputs').get(input.input_name).value
  //             if (input.input_type === 'pct' && value) {
  //               value = value / 100
  //             }
  //             return {
  //               input_name: input.input_name,
  //               input_value: value
  //             }
  //           })
  //         }
  //       })
  //     }

  //     console.log('rule', rule)

  //     if (this.config.data.mode === 'edit') {
  //       await this.editRule(rule)
  //     } else {
  //       await this.addRule(rule)
  //     }

  //     this.close()
  //   }

  getParentInputs (parent: AbstractControl): FormArray {
    return parent.get('parent_inputs') as FormArray
  }

  displayParentOptions (option: ParentChoice): string {
    return option.parent_title
  }
  // displayActionInputOptions (option): string {
  //   console.log('displayactioninoput', option)
  //   return option.value
  // }

  onParentSelected (event: MatAutocompleteSelectedEvent, index: number) {
    console.log('event.option.value', event.option.value)
    const selectedOption = event.option.value as ParentChoice
    const parent = this.parents[index]
    // Update the condition object with the selected option's properties
    parent.parent_id = selectedOption.parent_id
    parent.parent_title = selectedOption.parent_title
    parent.allowedActions = selectedOption.allowed_actions ?? null

    // Recompute allowedActions from all parents from scratch
    this.allowedActions = null
    this.parents.forEach(p => {
      if (p.allowedActions) {
        if (this.allowedActions?.length) {
          this.allowedActions = this.allowedActions.filter(action => p.allowedActions.includes(action))
        } else {
          this.allowedActions = p.allowedActions
        }
      }
    })
    this.updateAllowedActions.emit(this.allowedActions)

    this.addParentInputs(selectedOption.inputs, parent)

    console.log('this.parents', this.parents)

    parent.formGroup.updateValueAndValidity()

    // const actionsArray = this.ruleForm.get('actions') as FormArray
    // const actionGroup = actionsArray.at(index) as FormGroup

    // // Update the form controls with the selected option's properties
    // actionGroup.patchValue({
    //   action_id: selectedOption.action_id,
    //   action_name: selectedOption.action_title,
    //   action_execution_order: index + 1,
    //   options: selectedOption.options
    // })

    // Add the correct number of inputs with the correct names, etc.
    this.filteredParentChoices = this.parentChoices

    if (parent.inputs) {
      parent.inputs.forEach((input: any) => {
        if (input.options) {
          const key = `${index}-${input.input_name}`
          console.log(
            'adding filtered parent input options',
            key,
            input.options,
            this.filteredParentInputOptions
          )
          this.filteredParentInputOptions[key] = this.getConditionalInputOptions(input, parent)
        }
      })
    }

    // console.log('event option', event.option)
    // const selectedOption = event.option.value as ParentChoice
    // const condition = this.conditions[index]
    // console.log(condition.formGroup)
    // if (condition.formGroup.contains('value')) {
    //   condition.formGroup.get('value').reset()
    // }
    // if (condition.formGroup.contains('value2')) {
    //   condition.formGroup.get('value2').reset()
    // }
    // // Update the condition object with the selected option's properties
    // if (condition.parent_id && condition.parent_id === selectedOption.parent_id) {
    //   // Add value as condition input
    //   this.addConditionInput(condition, selectedOption, true)
    // } else {
    //   // new condition
    //   condition.parent_id = selectedOption.parent_id
    //   condition.parent_title = selectedOption.condition
    //   condition.input_name = 'value'
    //   condition.options = selectedOption.options
    //   condition.datatype = selectedOption.datatype

    //   if (Object.hasOwn(selectedOption, 'input_name2')) {

    //   }

    //   // Add a new condition input
    //   this.addConditionInput(condition, selectedOption)
    // }
    // this.filteredParentChoices = this.parentChoices

    // const selectedCondition = event.option.value
    // if (selectedCondition.options) {
    //   this.filteredConditionValueOptions[index] = selectedCondition.options
    // }
  }

  onParentInputSelected (e, parentIndex: number, inputName: string) {
    console.log('parent input selected', e)
    const parent = this.parents[parentIndex]
    const input = parent.inputs?.find((inp: any) => inp.input_name === inputName)

    if (parent.inputs.length > 1) {
      console.log('parent.inputs.length > 1')
      if (parent.inputs.findIndex(p => p.input_name === inputName) === 0) {
        console.log('second input found')
        const secondInput = parent.inputs[1]
        // first input
        // filter second
        console.log('forcing this.filteredparentinputoptions update')
        this.filteredParentInputOptions[`${parentIndex}-${secondInput.input_name}`] =
                    this.getConditionalInputOptions(secondInput, parent)
        this.filteredParentInputOptions = JSON.parse(JSON.stringify(this.filterParentInputOptions))
      }
    }

    console.log('parent after selection', parent)
    console.log('input after selection', input)

    // if (!input?.options) {
    //   return
    // }

    // const key = `${parentIndex}-${inputName}`

    // if (searchTerm) {
    //   this.filteredParentInputOptions[key] = this.getConditionalInputOptions(input, parent).filter((option: string) =>
    //     option.toLowerCase().includes(searchTerm.toLowerCase())
    //   )
    // } else {
    //   this.filteredParentInputOptions[key] =
    // }
  }

  filterParentOptions (searchTerm: string): void {
    if (!searchTerm) {
      this.filteredParentChoices = this.parentChoices
      return
    }

    this.filteredParentChoices = this.parentChoices.filter(option =>
      option.parent_title.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }

  onParentInputChange (event: any, index: number): void {
    this.parentOptionsSearchTerm = event.target.value
    this.filterParentOptions(this.parentOptionsSearchTerm)
  }

  onParentValueChanged (event, index) {
    console.log('conditionvaluechanged', event)
  }

  //   filterParentValueOptions (searchTerm: string, parentIndex: number, inputIndex: input): void {
  //     const parent = this.parents[parentIndex]

  //     if (!parent.options) {
  //       return
  //     }

  //     if (!searchTerm) {
  //       this.filteredConditionValueOptions[conditionIndex] = condition.options
  //       return
  //     }

  //     this.filteredConditionValueOptions[conditionIndex] = condition.options.filter((option: string) =>
  //       option.toLowerCase().includes(searchTerm.toLowerCase())
  //     )
  //   }

  //   onConditionValueInputChange (event: any, conditionIndex: number): void {
  //     const searchTerm = event.target.value
  //     this.filterConditionValueOptions(searchTerm, conditionIndex)
  //   }

  //   onConditionInputOptionSelected(event: any, condition: ParentChoice): void {
  //     if (parentChoice)
  //   }

  //   getFilteredValueOptions (index: number): any[] {
  //     return this.filteredConditionValueOptions[index] || []
  //   }

  //   onActionValueChanged (event, index) {
  //     console.log(event)
  //   }

  //   onActionSelected (event: MatAutocompleteSelectedEvent, index: number) {
  //     console.log('event.option.value', event.option.value)
  //     const selectedOption = event.option.value as ActionChoice
  //     const action = this.actions[index]
  //     // Update the condition object with the selected option's properties
  //     action.action_id = selectedOption.action_id
  //     action.action_name = selectedOption.action_title

  //     this.addActionInputs(selectedOption.inputs, action)

  //     console.log('this.actions', this.actions)

  //     action.formGroup.updateValueAndValidity()

  //     // const actionsArray = this.ruleForm.get('actions') as FormArray
  //     // const actionGroup = actionsArray.at(index) as FormGroup

  //     // // Update the form controls with the selected option's properties
  //     // actionGroup.patchValue({
  //     //   action_id: selectedOption.action_id,
  //     //   action_name: selectedOption.action_title,
  //     //   action_execution_order: index + 1,
  //     //   options: selectedOption.options
  //     // })

  //     // Add the correct number of inputs with the correct names, etc.
  //     this.filteredActionChoices = this.actionChoices

  //     if (action.inputs) {
  //       action.inputs.forEach((input: any) => {
  //         if (input.options) {
  //           const key = `${index}-${input.input_name}`
  //           console.log(
  //             'adding filtered action input options',
  //             key,
  //             input.options,
  //             this.filteredActionInputOptions
  //           )
  //           this.filteredActionInputOptions[key] = [...input.options]
  //         }
  //       })
  //     }
  //   }

  //   addActionInputs (inputs: ActionInput[], action: Action, actionChoice: ActionChoice = undefined) {
  //     const inputFormGroup = action.formGroup.get('inputs') as FormGroup
  //     action.inputs.forEach(input => {
  //       inputFormGroup.removeControl(input.input_name)
  //     })
  //     action.formGroup.addControl('inputs', inputFormGroup)
  //     action.inputs = []
  //     console.log('adding all action inputs', inputs)
  //     for (let i = 0; i < inputs.length; i++) {
  //       const input = inputs[i]
  //       const validators = this.getValidators(input.input_type)

  //       console.log('input.options', input.options)

  //       console.log('adding control of name', input.input_name)

  //       inputFormGroup.addControl(
  //         input.input_name,
  //         new FormControl(actionChoice ? input.input_value : '', { validators })
  //       )

  //       console.log('added form control', inputFormGroup.get(input.input_name))

  //       action.inputs.push({
  //         input_name: input.input_name,
  //         input_type: input.input_type,
  //         options: inputs[i].options
  //       })
  //     }
  //   }

  //   filterActionOptions (searchTerm: string): void {
  //     if (!searchTerm) {
  //       this.filteredActionChoices = this.actionChoices
  //       return
  //     }

  //     this.filteredActionChoices = this.actionChoices.filter(option =>
  //       option.action_title.toLowerCase().includes(searchTerm.toLowerCase())
  //     )
  //   }

  // filterConditionOptions (searchTerm: string): void {
  //   if (!searchTerm) {
  //     this.filteredParentChoices = this.parentChoices
  //     return
  //   }

  //   this.filteredParentChoices = this.parentChoices.filter(option =>
  //     option.parent_title.toLowerCase().includes(searchTerm.toLowerCase())
  //   )
  // }

  //   onParentInputChange (event: any, index: number): void {
  //     const searchTerm = event.target.value
  //     this.filterParentOptions(searchTerm)
  //   }

  onParentMultiselectInputValueChanged (event: any, parentIndex: number, inputName: string): void {
    console.log('event from pauto change', event)
    const searchTerm = event.query
    const key = `${parentIndex}-${inputName}`
    this.activeQueries[key] = searchTerm
    setTimeout(() => this.filterParentInputOptions(searchTerm, parentIndex, inputName), 0)
  }

  removeProductId (input: ParentInput, keyword: string) {
    input.chips.update(keywords => {
      const index = keywords.indexOf(keyword)
      if (index < 0) {
        return keywords
      }

      keywords.splice(index, 1)
      this.announcer.announce(`removed ${keyword} from template form`)
      return [...keywords]
    })
  }

  addProductId (event: MatChipInputEvent, input: ParentInput): void {
    const value = (event.value || '').trim()

    // Add our keyword
    if (value) {
      input.chips.update(keywords => [...keywords, value])
      this.announcer.announce(`added ${value} to template form`)
    }

    // Clear the input value
    event.chipInput?.clear()
  }

  filterParentInputOptions (searchTerm: string, parentIndex: number, inputName: string): any[] {
    const parent = this.parents[parentIndex]
    const input = parent.inputs?.find((inp: any) => inp.input_name === inputName)

    if (!input?.options) {
      return []
    }

    const key = `${parentIndex}-${inputName}`

    if (input.options === 'sizes') {
      const { items, totalCount } = this.sizesService.getPage(searchTerm, 0, 100)
      const arr: string[] = new Array(totalCount).fill('')
      for (let idx = 0; idx < items.length; idx++) arr[idx] = items[idx]
      this.filteredParentInputOptions[key] = arr
    } else if (searchTerm) {
      this.filteredParentInputOptions[key] = this.getConditionalInputOptions(input, parent).filter(
        (option: string) => option.toLowerCase().includes(searchTerm.toLowerCase())
      )
    } else {
      this.filteredParentInputOptions[key] = this.getConditionalInputOptions(input, parent)
    }
    console.log('returning this.filteredParentInputOptions[key]', this.filteredParentInputOptions[key])
    return this.filteredParentInputOptions[key]
  }

  onSizesLazyLoad (event: any, parentIndex: number, inputName: string): void {
    const key = `${parentIndex}-${inputName}`
    const query = this.activeQueries[key] ?? ''
    const { items } = this.sizesService.getPage(query, event.first, event.last)
    const arr = [...(this.filteredParentInputOptions[key] ?? [])]
    for (let i = 0; i < items.length; i++) arr[event.first + i] = items[i]
    this.filteredParentInputOptions[key] = arr
  }

  // getFilteredParentInputOptions (parentIndex: number, inputName: string): any[] {
  //   const key = `${parentIndex}-${inputName}`
  //   const parent = this.parents[parentIndex]
  //   const input = parent.inputs?.find((inp: any) => inp.input_name === inputName)

  //   // If no filtered options exist yet, return the original options
  //   if (!this.filteredParentInputOptions[key] && input?.options) {
  //     this.filteredParentInputOptions[key] = this.getConditionalInputOptions(input, parent)
  //   }

  //   return this.filteredParentInputOptions[key] || []
  // }

  getConditionalInputOptions (input: ParentInput, parent: Parent, setValueNull = true) {
    console.log('checking is array for options', input.options)
    console.log('typeof input.options[0]', typeof input.options[0])
    if (input.options.length && typeof input.options[0] === 'object') {
      console.log('parent.formGroup.controls.inputs.value', parent.formGroup.controls.inputs.value)
      console.log('parent.inputs[0].input_name', parent.inputs[0].input_name)
      const value = parent.formGroup.controls.inputs.value[parent.inputs[0].input_name]
      console.log('value is', value)
      if (value) {
        console.log('input options list should be', input.options[0][value])
      }
      console.log('inputs controls', parent.formGroup.get('inputs'))
      // if (setValueNull) parent.formGroup.get('inputs').get(input.input_name).setValue(null)
      return value ? [...input.options[0][value]] : []
    } else if (input.options === 'sizes') {
      console.log('this.sizes', this.sizes)
      return this.sizes
    } else {
      return [...input.options]
    }
  }

  onClearMultiselect (event: Event, parent: Parent, input: ParentInput) {
    console.log('parent', parent)
    console.log('parent.formGroup.controls.inputs', parent.formGroup.controls.inputs)
    if (parent.inputs.length > 1 && input.input_name === parent.inputs[0].input_name) {
      parent.formGroup.get('inputs').get(parent.inputs[1].input_name).setValue([])
    }
  }

  getValidators (datatype: string): ValidatorFn[] {
    const validators: ValidatorFn[] = []

    switch (datatype) {
      case 'boolean':
        break
      case 'integer':
      case 'number':
        validators.push(Validators.required)
        validators.push(CheckIsInteger())
        break
      case 'float':
      case 'pct':
        validators.push(Validators.required)
        validators.push(CheckIsFloat())
        break
      default:
        validators.push(Validators.required)
        break
    }
    validators.push(this.checkChildErrors())

    return validators
  }

  //   toggleRunRule () {
  //     this.runRule = !this.runRule
  //     // console.log('ruleForm', this.ruleForm)
  //     // console.log('conditions', this.conditions)
  //     // console.log('actions', this.actions)
  //     // console.log('controls', this.ruleForm.controls)
  //     // console.log('this.actionFormArray', this.actionFormArray)
  //     // console.log('this.conditionFormArray', this.conditionFormArray)
  //     // this.actions.forEach(action => action.inputs.forEach(input => input.form.updateValueAndValidity()))
  //     // this.conditions.forEach(condition => condition.formGroup.updateValueAndValidity())
  //     // this.actions.forEach(action => action.formGroup.updateValueAndValidity())
  //   }

  // checkChildErrors () {
  //   const valFunc = (control: AbstractControl): ValidationErrors | null => {
  //     console.log('checkchilderrors', control)
  //     if (control.errors) {
  //       return control.errors
  //     } else if (control['controls']) {
  //       console.log('control.controls', control['controls'])
  //       return Object.values(control['controls']).map(control => valFunc(control as any)).filter(val => val)[0]
  //     } else {
  //       return null
  //     }
  //   }
  //   return valFunc
  // }

  checkChildErrors () {
    const valFunc = (control: AbstractControl): ValidationErrors | null => {
      if (control['controls']) {
        let newErrors = {}
        // if (control.errors) {
        //   newErrors = { ...control.errors }
        // } else {
        //   newErrors = {}
        // }
        for (const c of Object.entries(control['controls'])) {
          const childControl = c[1] as AbstractControl
          if (childControl.errors) {
            newErrors = {
              ...childControl.errors,
              ...newErrors
            }
          }
        }
        return Object.entries(newErrors).length ? newErrors : null
      }
    }
    return valFunc
  }

  focusBlurInput (event, multiple: boolean, focus: boolean) {
    console.log('focused event', event)
    const target = multiple
      ? event.target.parentElement.parentElement.parentElement.parentElement.parentElement.parentElement
      : event.target.parentElement.parentElement.parentElement.parentElement
    console.log('targeting element', target)
    target.className = focus
      ? target.className + ' autocomplete-input-focus'
      : target.className.replaceAll(' autocomplete-input-focus', '')
  }

  getToolTip (input: any): string {
    let tooltip = ''
    if ('parent_title' in input) {
      if (input.datatype === 'boolean') {
        tooltip = 'Please Enter True or False'
      } else if (input.datatype === 'integer') {
        tooltip = 'Please Enter A Number Value'
      } else if (input.datatype === 'pct') {
        tooltip = 'Please Enter A Percentage Value'
      } else if (
        input.parent_title === 'Size range has <= N sizes' ||
                input.parent_title === 'Size range has >= N sizes'
      ) {
        tooltip = 'Please Enter A Value'
      } else if (input.datatype === 'float') {
        tooltip = 'Please Enter A Float Value'
      } else if (input.parent_title.toLowerCase().includes('size range is')) {
        tooltip = 'Please Select A Size Range'
      } else if (input.parent_title.toLowerCase().includes('size')) {
        tooltip = 'Please Select A Size'
      } else if (input.parent_title.toLowerCase().includes('season')) {
        tooltip = 'Please Select A Season'
      } else if (input.parent_title.toLowerCase().includes('product')) {
        tooltip = 'Please Enter A Product'
      } else if (
        input.parent_title.toLowerCase().includes('profile') &&
                input.parent_title.toLowerCase().includes('created by')
      ) {
        tooltip = 'Please Select An Email'
      } else if (input.parent_title.toLowerCase().includes('profile')) {
        tooltip = 'Please Select A Channel'
      } else {
        tooltip = 'Please Enter A Value'
      }
    } else {
      if (input.input_type === 'boolean' || input.input_name === 'TRUE') {
        tooltip = 'Please Enter True or False'
      } else if (input.input_type === 'integer') {
        tooltip = 'Please Enter A Number'
      } else if (input.input_type === 'float') {
        tooltip = 'Please Enter A Float Value'
      } else if (input.input_type === 'pct') {
        tooltip = 'Please Enter A Percentage Value'
      } else if (input.input_name === 'Size') {
        tooltip = 'Please Select A Size'
      } else if (input.input_name === 'Old Size') {
        tooltip = 'Please Select The Old Size'
      } else if (input.input_name === 'Old Size 1') {
        tooltip = 'Please Select The First Old Size'
      } else if (input.input_name === 'Old Size 2') {
        tooltip = 'Please Select The Second Old Size'
      } else if (input.input_name === 'New Size') {
        tooltip = 'Please Select The New Size'
      } else if (input.input_name === 'New Size 1') {
        tooltip = 'Please Select The First New Size'
      } else if (input.input_name === 'New Size 2') {
        tooltip = 'Please Select The Second New Size'
      } else if (input.input_name.includes('Store')) {
        tooltip = 'Please Select A Store'
      } else if (input.input_name === 'Season') {
        tooltip = 'Please Select A Season'
      } else if (input.input_name === 'To Style-Colors Under Parent Product') {
        tooltip = 'Please Enter A Style-Colors Under Parent Product'
      } else if (input.input_name.toLowerCase().includes('product')) {
        tooltip = 'Please Enter A Product'
      } else if (input.input_name === 'Value' || input.input_type === 'text') {
        tooltip = 'Please Enter A Value'
      }
    }
    return tooltip
  }
}
